import React, { useState, useRef, useEffect } from 'react';

type NumberInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
> & {
  value: number | null | undefined;
  onChange: (value: number | undefined) => void;
  /** Restrict to integers (default false → allows decimals). */
  integer?: boolean;
  /** Clamp to min/max on blur (default true). */
  clampOnBlur?: boolean;
  /** Value committed when the field is left empty on blur (default undefined). */
  emptyValue?: number | undefined;
};

/**
 * Controlled numeric input that keeps a local string buffer while editing, so the
 * field can be fully cleared and retyped (fixes the "can't delete the 0" bug that
 * happens when a number is coerced on every keystroke). Parsing/clamping happens
 * on blur; partial input like "-" or "1." is tolerated mid-edit.
 */
export default function NumberInput({
  value,
  onChange,
  integer = false,
  clampOnBlur = true,
  emptyValue,
  min,
  max,
  onBlur,
  onFocus,
  ...rest
}: NumberInputProps) {
  const [text, setText] = useState<string>(value == null ? '' : String(value));
  const focused = useRef(false);

  // Reflect external value changes (e.g. reset buttons) while not actively editing.
  useEffect(() => {
    if (!focused.current) setText(value == null ? '' : String(value));
  }, [value]);

  const parse = (s: string): number | undefined => {
    if (s.trim() === '') return undefined;
    const n = integer ? parseInt(s, 10) : parseFloat(s);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setText(raw);
    if (raw.trim() === '') {
      onChange(emptyValue);
      return;
    }
    const n = parse(raw);
    // Emit only a fully valid number; ignore partial input like "-" or "1." so the
    // user can keep typing without the value snapping.
    if (n !== undefined) onChange(n);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    focused.current = false;
    let n = parse(text);
    if (n === undefined) {
      onChange(emptyValue);
      setText(emptyValue == null ? '' : String(emptyValue));
    } else {
      if (clampOnBlur) {
        if (min !== undefined && n < Number(min)) n = Number(min);
        if (max !== undefined && n > Number(max)) n = Number(max);
      }
      onChange(n);
      setText(String(n));
    }
    onBlur?.(e);
  };

  return (
    <input
      {...rest}
      type="number"
      inputMode={integer ? 'numeric' : 'decimal'}
      min={min}
      max={max}
      value={text}
      onFocus={(e) => { focused.current = true; onFocus?.(e); }}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
